/**
 * Legacy localStorage -> cloud migration (CONTRACTS.md §3, spec §11).
 *
 * The pre-vNext app persisted everything via zustand/persist under
 * 'rust-app-storage' (spec §4.1, shapes §4.3). This module:
 *
 * - detectLegacyLocalData(): parses that payload and returns item counts;
 * - importLegacyLocalData(): preview-then-confirm import that
 *   · maps legacy ids to cloud ids (stored in the idempotency marker),
 *   · is idempotent: marker 'rust-legacy-import-v1' + content hash, so a
 *     re-run imports nothing twice;
 *   · keeps a local backup copy under 'rust-legacy-backup' (retained after
 *     a confirmed import — the user can delete it from the Data page);
 *   · converts legacy field shapes (planner hour/quarter -> startMinute,
 *     place.room -> category).
 *
 * Import writes go through `repositories`, so a missing session or missing
 * Supabase config surfaces as the honest typed error — never a fake import.
 */
import { makeAppError, newCorrelationId } from '@/lib/supabase/errors'
import { repositories } from '@/services/repositories'
import type {
  HealthItemType,
  NewJournalEntry,
  NewNote,
  NewPlace,
  NewPlannerItem,
} from '@/types'

export const LEGACY_STORAGE_KEY = 'rust-app-storage'
export const LEGACY_BACKUP_KEY = 'rust-legacy-backup'
export const LEGACY_IMPORT_MARKER_KEY = 'rust-legacy-import-v1'

export type LegacyLocalSummary = {
  plannerItems: number
  notes: number
  places: number
  journalEntries: number
  healthItems: number
  breathingSessions: number
}

export type LegacyImportResult = {
  imported: number
  skipped: number
  backupKey: string
}

export type LegacyImportProgress = {
  done: number
  total: number
}

type LegacyPlannerItem = {
  id?: unknown
  date?: unknown
  hour?: unknown
  quarter?: unknown
  duration?: unknown
  activityId?: unknown
  activityName?: unknown
  category?: unknown
  color?: unknown
  iconId?: unknown
  notes?: unknown
}

type LegacyNote = {
  id?: unknown
  title?: unknown
  content?: unknown
  category?: unknown
  pinned?: unknown
  urgent?: unknown
  done?: unknown
  archived?: unknown
}

type LegacyPlace = {
  id?: unknown
  room?: unknown
  objectLabel?: unknown
  subzone?: unknown
  notes?: unknown
}

type LegacyJournalEntry = {
  id?: unknown
  date?: unknown
  mood?: unknown
  energy?: unknown
  stress?: unknown
  wentWell?: unknown
  wasDifficult?: unknown
  rememberTomorrow?: unknown
  freewriting?: unknown
  tags?: unknown
}

type LegacyHealthItem = {
  id?: unknown
  name?: unknown
  type?: unknown
  dosage?: unknown
  schedule?: unknown
}

type LegacyBreathingSession = {
  id?: unknown
  exerciseId?: unknown
  durationSeconds?: unknown
  completed?: unknown
  interrupted?: unknown
  startedAt?: unknown
  finishedAt?: unknown
}

type LegacyState = {
  plannerItems: LegacyPlannerItem[]
  notes: LegacyNote[]
  places: LegacyPlace[]
  journalEntries: LegacyJournalEntry[]
  healthItems: LegacyHealthItem[]
  breathingSessions: LegacyBreathingSession[]
}

type ImportMarker = {
  hash: string
  importedAt: string
  /** legacy id -> cloud id, per collection; re-runs skip mapped ids. */
  importedIds: Record<string, Record<string, string>>
}

// ---------------------------------------------------------------------------
// Parsing helpers (tolerant: unknown/malformed legacy data never throws here)
// ---------------------------------------------------------------------------

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function pickArray(state: Record<string, unknown>, ...keys: string[]): unknown[] {
  for (const key of keys) {
    if (Array.isArray(state[key])) return state[key] as unknown[]
  }
  return []
}

/** Extracts the zustand/persist envelope `{ state, version }` if present. */
function readLegacyState(): { raw: string; state: LegacyState } | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(LEGACY_STORAGE_KEY)
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null
  const envelope = parsed as Record<string, unknown>
  const stateCandidate =
    typeof envelope.state === 'object' && envelope.state !== null
      ? (envelope.state as Record<string, unknown>)
      : envelope

  return {
    raw,
    state: {
      plannerItems: asArray(pickArray(stateCandidate, 'plannerItems', 'planner', 'items')) as LegacyPlannerItem[],
      notes: asArray(pickArray(stateCandidate, 'notes')) as LegacyNote[],
      places: asArray(pickArray(stateCandidate, 'places')) as LegacyPlace[],
      journalEntries: asArray(pickArray(stateCandidate, 'journalEntries', 'journal')) as LegacyJournalEntry[],
      healthItems: asArray(pickArray(stateCandidate, 'healthItems', 'medications')) as LegacyHealthItem[],
      breathingSessions: asArray(
        pickArray(stateCandidate, 'breathingSessions', 'breathing', 'sessions'),
      ) as LegacyBreathingSession[],
    },
  }
}

/** FNV-1a 32-bit content hash — stable idempotency fingerprint, not security. */
export function hashLegacyContent(raw: string): string {
  let hash = 0x811c9dc5
  for (let index = 0; index < raw.length; index += 1) {
    hash ^= raw.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function readMarker(): ImportMarker | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(LEGACY_IMPORT_MARKER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ImportMarker>
    if (typeof parsed.hash !== 'string' || typeof parsed.importedIds !== 'object' || parsed.importedIds === null) {
      return null
    }
    return {
      hash: parsed.hash,
      importedAt: typeof parsed.importedAt === 'string' ? parsed.importedAt : '',
      importedIds: parsed.importedIds as Record<string, Record<string, string>>,
    }
  } catch {
    return null
  }
}

function writeMarker(marker: ImportMarker): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(LEGACY_IMPORT_MARKER_KEY, JSON.stringify(marker))
  } catch {
    // Marker persistence failing is surfaced by the next run re-checking ids.
  }
}

function importError(error: unknown): never {
  if (typeof error === 'object' && error !== null && 'code' in error) throw error
  throw makeAppError({
    code: 'LEGACY_IMPORT_FAILED',
    userMessage: 'Het importeren is niet gelukt. Je lokale gegevens zijn bewaard gebleven.',
    technicalMessage: error instanceof Error ? error.message : 'unknown legacy import failure',
    retryable: true,
    source: 'database',
    correlationId: newCorrelationId(),
  })
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function detectLegacyLocalData(): LegacyLocalSummary | null {
  const parsed = readLegacyState()
  if (!parsed) return null
  const { state } = parsed
  const summary: LegacyLocalSummary = {
    plannerItems: state.plannerItems.length,
    notes: state.notes.length,
    places: state.places.length,
    journalEntries: state.journalEntries.length,
    healthItems: state.healthItems.length,
    breathingSessions: state.breathingSessions.length,
  }
  const total = Object.values(summary).reduce((sum, count) => sum + count, 0)
  return total > 0 ? summary : null
}

export async function importLegacyLocalData(
  onProgress?: (progress: LegacyImportProgress) => void,
): Promise<LegacyImportResult> {
  const parsed = readLegacyState()
  if (!parsed) return { imported: 0, skipped: 0, backupKey: LEGACY_BACKUP_KEY }

  const hash = hashLegacyContent(parsed.raw)
  const { state } = parsed
  const summary: LegacyLocalSummary = {
    plannerItems: state.plannerItems.length,
    notes: state.notes.length,
    places: state.places.length,
    journalEntries: state.journalEntries.length,
    healthItems: state.healthItems.length,
    breathingSessions: state.breathingSessions.length,
  }
  const total = Object.values(summary).reduce((sum, count) => sum + count, 0)

  const existingMarker = readMarker()
  if (existingMarker && existingMarker.hash === hash) {
    // Identical payload already imported: never duplicate on re-run.
    return { imported: 0, skipped: total, backupKey: LEGACY_BACKUP_KEY }
  }

  // Local backup copy is written BEFORE the first cloud write and retained
  // after the confirmed import (deletion is an explicit user action later).
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(LEGACY_BACKUP_KEY, parsed.raw)
    } catch {
      throw makeAppError({
        code: 'LEGACY_BACKUP_FAILED',
        userMessage: 'Er kon geen lokale back-up worden gemaakt. De import is niet gestart.',
        retryable: true,
        source: 'browser',
        correlationId: newCorrelationId(),
      })
    }
  }

  const marker: ImportMarker = existingMarker ?? {
    hash,
    importedAt: '',
    importedIds: {},
  }
  marker.hash = hash

  let imported = 0
  let skipped = 0
  let done = 0
  const report = () => onProgress?.({ done, total })

  const alreadyImported = (collection: string, legacyId: string): boolean =>
    Boolean(marker.importedIds[collection]?.[legacyId])

  const recordMapping = (collection: string, legacyId: string, cloudId: string) => {
    if (!marker.importedIds[collection]) marker.importedIds[collection] = {}
    marker.importedIds[collection][legacyId] = cloudId
    // Incremental marker writes keep a crashed import resumable.
    writeMarker(marker)
  }

  try {
    for (const item of state.plannerItems) {
      done += 1
      const legacyId = asString(item.id)
      if (legacyId && alreadyImported('plannerItems', legacyId)) {
        skipped += 1
        report()
        continue
      }
      const date = asString(item.date)
      if (!date) {
        skipped += 1
        report()
        continue
      }
      const startMinute = Math.min(
        1439,
        Math.max(0, asNumber(item.hour, 0) * 60 + asNumber(item.quarter, 0) * 15),
      )
      const newItem: NewPlannerItem = {
        date,
        startMinute,
        durationMinutes: Math.max(5, asNumber(item.duration, 30)),
        activityId: asString(item.activityId, 'legacy'),
        activityName: asString(item.activityName, 'Activiteit'),
        category: asString(item.category, 'Algemeen'),
        color: asString(item.color, '#8DA6C1'),
        iconId: asString(item.iconId, 'default'),
        source: 'manual',
        ...(asString(item.notes) ? { notes: asString(item.notes) } : {}),
      }
      const created = await repositories.planner.create(newItem)
      imported += 1
      if (legacyId) recordMapping('plannerItems', legacyId, created.id)
      report()
    }

    for (const note of state.notes) {
      done += 1
      const legacyId = asString(note.id)
      if (legacyId && alreadyImported('notes', legacyId)) {
        skipped += 1
        report()
        continue
      }
      const newNote: NewNote = {
        title: asString(note.title, 'Notitie'),
        content: asString(note.content),
        category: asString(note.category, 'Algemeen'),
        pinned: asBoolean(note.pinned),
        urgent: asBoolean(note.urgent),
        done: asBoolean(note.done),
        archived: asBoolean(note.archived),
      }
      const created = await repositories.notes.create(newNote)
      imported += 1
      if (legacyId) recordMapping('notes', legacyId, created.id)
      report()
    }

    for (const place of state.places) {
      done += 1
      const legacyId = asString(place.id)
      if (legacyId && alreadyImported('places', legacyId)) {
        skipped += 1
        report()
        continue
      }
      // Legacy imageUrl values are local/object URLs and cannot be
      // re-uploaded; the photo must be added again in the app.
      const newPlace: NewPlace = {
        category: asString(place.room, 'Anders'),
        objectLabel: asString(place.objectLabel, 'Voorwerp'),
        ...(asString(place.subzone) ? { subzone: asString(place.subzone) } : {}),
        ...(asString(place.notes) ? { notes: asString(place.notes) } : {}),
      }
      const created = await repositories.places.create(newPlace)
      imported += 1
      if (legacyId) recordMapping('places', legacyId, created.id)
      report()
    }

    for (const entry of state.journalEntries) {
      done += 1
      const legacyId = asString(entry.id)
      if (legacyId && alreadyImported('journalEntries', legacyId)) {
        skipped += 1
        report()
        continue
      }
      const date = asString(entry.date)
      if (!date) {
        skipped += 1
        report()
        continue
      }
      const newEntry: NewJournalEntry = {
        date,
        mood: Math.min(5, Math.max(1, asNumber(entry.mood, 3))),
        energy: Math.min(5, Math.max(1, asNumber(entry.energy, 3))),
        stress: Math.min(5, Math.max(1, asNumber(entry.stress, 3))),
        wentWell: asString(entry.wentWell),
        wasDifficult: asString(entry.wasDifficult),
        rememberTomorrow: asString(entry.rememberTomorrow),
        freewriting: asString(entry.freewriting),
        tags: asStringArray(entry.tags),
      }
      // save() upserts on (user_id, date): re-running the same date is safe.
      const saved = await repositories.journal.save(newEntry)
      imported += 1
      if (legacyId) recordMapping('journalEntries', legacyId, saved.id)
      report()
    }

    for (const item of state.healthItems) {
      done += 1
      const legacyId = asString(item.id)
      if (legacyId && alreadyImported('healthItems', legacyId)) {
        skipped += 1
        report()
        continue
      }
      const rawType = asString(item.type)
      const type: HealthItemType =
        rawType === 'supplement' || rawType === 'medication' ? rawType : 'other'
      const created = await repositories.health.createItem({
        name: asString(item.name, 'Item'),
        type,
        dosage: asString(item.dosage),
        schedule: asString(item.schedule),
      })
      imported += 1
      if (legacyId) recordMapping('healthItems', legacyId, created.id)
      report()
    }

    for (const session of state.breathingSessions) {
      done += 1
      const legacyId = asString(session.id)
      if (legacyId && alreadyImported('breathingSessions', legacyId)) {
        skipped += 1
        report()
        continue
      }
      const startedAt = asString(session.startedAt)
      const exerciseId = asString(session.exerciseId)
      if (!startedAt || !exerciseId) {
        skipped += 1
        report()
        continue
      }
      const finishedAtRaw = asString(session.finishedAt)
      const saved = await repositories.breathing.saveSession({
        exerciseId,
        durationSeconds: Math.max(0, asNumber(session.durationSeconds, 0)),
        completed: asBoolean(session.completed, true),
        interrupted: asBoolean(session.interrupted),
        startedAt,
        finishedAt: finishedAtRaw || null,
      })
      imported += 1
      if (legacyId) recordMapping('breathingSessions', legacyId, saved.id)
      report()
    }
  } catch (error) {
    importError(error)
  }

  marker.importedAt = new Date().toISOString()
  writeMarker(marker)
  return { imported, skipped, backupKey: LEGACY_BACKUP_KEY }
}
